/* eslint-disable no-bitwise */
import { Injectable } from '@angular/core';
import { ModbusLink, V3WizardSafetyCountryAndVersion } from 'ngx-modbus';

import { SafetyMetadata } from '../SGP';

const SAFETY_SVC_DEBUG = { ENABLED: false };
const safetyLog = (msg: string, data?: unknown) => SAFETY_SVC_DEBUG.ENABLED && console.log(`[Safety:Upgrade] ${msg}`, data ?? '');

const upgrade = async (
  safetyM: SafetyMetadata,
  command: ModbusLink,
  progress: (p: number, f: number, e: string, z: string[]) => void,
  cancellationSignal: { cancelled: boolean },
) => {
  const blocks = safetyM.parsedContent.blocks as { [key: string]: number[] };
  const BLOCK_SIZE = 64;
  const SPLIT_BLOCK_SIZE = 64;
  const MIN_BLOCK = 1;
  const MAX_BLOCK = 9;
  const START_ADDRESS = 0x0800;

  const validBlocks = Object.keys(blocks)
    .filter((block) => Number(block) >= MIN_BLOCK && Number(block) <= MAX_BLOCK);
  const totalBlocks = validBlocks.length;
  const phaseNames = ['Safety Processing', 'Upgrade Complete'];

  safetyLog(`START: ${totalBlocks} valid blocks (keys: ${validBlocks.join(',')}), country=${safetyM.parsedContent.country}, region=${safetyM.parsedContent.region}, ver=${safetyM.parsedContent.ver}`);
  safetyLog(`command type: ${command?.constructor?.name}, hasExecuteRaw=${typeof command?.executeRawOperation}`);

  let completedBlocks = 0;

  try {
    for (const blockKey of validBlocks) {
      if (cancellationSignal.cancelled) {
        throw new Error('Upgrade cancelled');
      }

      progress((completedBlocks / totalBlocks) * 100, 0, '', phaseNames);

      const content = blocks[blockKey];
      if (!Array.isArray(content) || content.length !== BLOCK_SIZE) {
        throw new Error('Invalid safety data');
      }

      const blockBaseAddress = START_ADDRESS + (BLOCK_SIZE * (Number(blockKey) - 1));

      const chunks = Math.ceil(content.length / SPLIT_BLOCK_SIZE);

      for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
        // TODO: Remove this check when the firmware is fixed
        /** It seems a buffer problem in writing on Solarman Stick Logger */
        if (chunkIndex > 0) {
          break;
        }

        const start = chunkIndex * SPLIT_BLOCK_SIZE;
        const end = start + SPLIT_BLOCK_SIZE;
        const chunk = content.slice(start, end);

        const register = chunk
          .map((value) => [ (value >> 8) & 0xFF, value & 0xFF ])
          .flat();

        const ADDRESS = blockBaseAddress + start;

        safetyLog(`Block ${blockKey}: writing ${register.length} bytes to address 0x${ADDRESS.toString(16)}`);
        const request = command.writeHoldingRegistersStandard(ADDRESS, new Uint8Array(register));
        safetyLog(`Block ${blockKey}: raw request built, executing...`);
        await command.executeRawOperation(request);
        safetyLog(`Block ${blockKey}: write OK`);
      }

      completedBlocks += 1;
    }

    const countryCode = Number(safetyM.parsedContent.country);
    const regionCode = Number(safetyM.parsedContent.region);
    const code = (countryCode << 8) | regionCode;
    const versionCode = Number(safetyM.parsedContent.ver);
    safetyLog(`Writing metadata: countryCode=${countryCode}, regionCode=${regionCode}, code=0x${code.toString(16)}, versionCode=${versionCode}`);
    await command.executeWriteMultipleHoldingRegisters([{
        key: 'Safety_Country_Code',
        value: code,
      }, {
        key: 'Safety_Version',
        value: versionCode,
      },
    ],
    V3WizardSafetyCountryAndVersion,
  );

    safetyLog('SUCCESS: all blocks written + metadata updated');
    progress(100, 1, '', phaseNames);
  } catch (error) {
    safetyLog(`ERROR: ${error instanceof Error ? error.message : error}`, error);
    if (error instanceof Error) {
      progress(0, 0, error.message, phaseNames);
    } else {
      progress(0, 0, 'Unknown error occurred', phaseNames);
    }
  }
};

@Injectable({
  providedIn: 'root',
})
export class SafetyService {

  readonly upgrade = upgrade;
}
